import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { isExternalRole, normalizeRole } from '../services/roleManagementService';

/**
 * Take A Lot Sales Leaderboard Component
 * Displays learners ranked by total sales amount
 */
const TakeALotLeaderboard = ({ compact = false }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, userProfile } = useAuth();
  const [scopedUserIds, setScopedUserIds] = useState(null);
  const isLearnerView = normalizeRole(userProfile?.role) === 'learner';

  useEffect(() => {
    const loadScope = async () => {
      try {
        if (!userProfile) return;
        const role = normalizeRole(userProfile.role);
        const isAdmin = role === 'admin' || role === 'qa';
        if (isAdmin) {
          setScopedUserIds(null);
          return;
        }

        if (role === 'learner') {
          // Show all learners on the sales leaderboard to increase competition,
          // but keep the learner-facing table limited to name + amount details only.
          setScopedUserIds(null);
          return;
        }

        if (role === 'facilitator' || isExternalRole(role)) {
          let cohortIds = Array.isArray(userProfile.assignedCohorts) ? userProfile.assignedCohorts : [];
          if (!cohortIds.length && Array.isArray(userProfile.assignedPrograms) && userProfile.assignedPrograms.length) {
            const cohortSnapshot = await getDocs(collection(db, 'cohorts'));
            cohortIds = cohortSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(cohort => userProfile.assignedPrograms.includes(cohort.programId))
              .map(cohort => cohort.id);
          }

          if (!cohortIds.length) {
            setScopedUserIds(new Set());
            return;
          }

          const userIds = new Set();
          for (let i = 0; i < cohortIds.length; i += 10) {
            const chunk = cohortIds.slice(i, i + 10);
            const usersSnapshot = await getDocs(
              query(collection(db, 'users'), where('role', '==', 'learner'), where('cohortId', 'in', chunk))
            );
            usersSnapshot.docs.forEach(doc => userIds.add(doc.id));
          }
          setScopedUserIds(userIds);
          return;
        }

        setScopedUserIds(null);
      } catch (error) {
        devError('Error loading leaderboard scope:', error);
        setScopedUserIds(null);
      }
    };

    loadScope();
  }, [userProfile]);

  useEffect(() => {
    fetchLeaderboard();
  }, [scopedUserIds]);

  /**
   * Fetch and aggregate sales data by user
   */
  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all sales records
      const salesQuery = query(collection(db, 'sales'));
      const snapshot = await getDocs(salesQuery);

      // Aggregate sales by user
      const userSalesMap = {};

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const userId = data.userId;
        if (scopedUserIds instanceof Set && !scopedUserIds.has(userId)) {
          return;
        }
        const userName = data.userName || 'Unknown';
        const userEmail = data.userEmail || '';
        const totalAmount = data.totalAmount || 0;
        const entryCount = data.entryCount || 0;
        const cohortId = data.cohortId || '';
        const cohortName = data.cohortName || '';

        if (!userSalesMap[userId]) {
          userSalesMap[userId] = {
            userId,
            userName,
            userEmail,
            totalSales: 0,
            transactionCount: 0,
            itemCount: 0,
            lastSubmission: null,
            cohortId,
            cohortName
          };
        }

        userSalesMap[userId].totalSales += totalAmount;
        userSalesMap[userId].transactionCount += 1;
        userSalesMap[userId].itemCount += entryCount;
        if (!userSalesMap[userId].cohortId && cohortId) {
          userSalesMap[userId].cohortId = cohortId;
        }
        if (!userSalesMap[userId].cohortName && cohortName) {
          userSalesMap[userId].cohortName = cohortName;
        }

        // Track last submission date
        const submittedAt = data.submittedAt?.toDate();
        if (submittedAt && (!userSalesMap[userId].lastSubmission || submittedAt > userSalesMap[userId].lastSubmission)) {
          userSalesMap[userId].lastSubmission = submittedAt;
        }
      });

      const userIds = Object.keys(userSalesMap);
      const userProfileMap = {};
      if (userIds.length > 0) {
        for (let i = 0; i < userIds.length; i += 10) {
          const chunk = userIds.slice(i, i + 10);
          const usersSnapshot = await getDocs(
            query(collection(db, 'users'), where('__name__', 'in', chunk))
          );
          usersSnapshot.docs.forEach(userDoc => {
            userProfileMap[userDoc.id] = userDoc.data();
          });
        }
      }

      let cohortNameMap = {};
      if (!isLearnerView) {
        const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
        cohortNameMap = cohortsSnapshot.docs.reduce((acc, docSnap) => {
          acc[docSnap.id] = docSnap.data().name || docSnap.data().cohortName || '';
          return acc;
        }, {});
      }

      // Convert to array and sort by total sales
      const leaderboard = Object.values(userSalesMap)
        .sort((a, b) => b.totalSales - a.totalSales)
        .map((user, index) => ({
          ...user,
          rank: index + 1,
          cohortId: user.cohortId || userProfileMap[user.userId]?.cohortId || '',
          cohortName: isLearnerView ? '' : (
            user.cohortName ||
            userProfileMap[user.userId]?.cohortName ||
            cohortNameMap[user.cohortId] ||
            (userProfileMap[user.userId]?.cohortId ? cohortNameMap[userProfileMap[user.userId].cohortId] : '') ||
            ''
          )
        }));

      setLeaderboardData(leaderboard);
    } catch (err) {
      devError('Error fetching sales leaderboard:', err);
      setError('Error loading leaderboard. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get rank badge styling
   */
  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge bg="warning" text="dark" className="px-2">🥇 1st</Badge>;
    if (rank === 2) return <Badge bg="secondary" className="px-2">🥈 2nd</Badge>;
    if (rank === 3) return <Badge bg="danger" className="px-2">🥉 3rd</Badge>;
    return <Badge bg="light" text="dark" className="px-2">#{rank}</Badge>;
  };

  /**
   * Get performance badge
   */
  const getPerformanceBadge = (totalSales) => {
    if (totalSales >= 50000) return <Badge bg="success">⭐ Star Performer</Badge>;
    if (totalSales >= 25000) return <Badge bg="info">💎 High Achiever</Badge>;
    if (totalSales >= 10000) return <Badge bg="primary">🚀 Rising Star</Badge>;
    return null;
  };

  // Compact view for embedding in dashboards
  if (compact) {
    return (
      <Card className="shadow-sm">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">🏆 Sales Leaderboard (Top 5)</h6>
          <Button variant="link" size="sm" onClick={fetchLeaderboard}>
            ↻ Refresh
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-3 mb-0">{error}</Alert>
          ) : (
            <Table hover responsive className="mb-0">
              <tbody>
                {leaderboardData.slice(0, 5).map((user) => (
                  <tr key={user.userId} className={currentUser?.uid === user.userId ? "table-primary" : ""}>
                    <td className="ps-3" style={{ width: '60px' }}>{getRankBadge(user.rank)}</td>
                    <td>
                      <strong>{user.userName}</strong>
                      {currentUser?.uid === user.userId && <small className="text-muted"> (You)</small>}
                      {user.cohortName && (
                        <div className="small text-muted">{user.cohortName}</div>
                      )}
                    </td>
                    <td className="text-end pe-3">
                      <strong className="text-success">R {user.totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    );
  }

  // Full view
  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-gradient bg-primary text-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">🏆 Take A Lot Sales Leaderboard</h5>
            <small>Ranked by total sales amount</small>
          </div>
          <Button variant="light" size="sm" onClick={fetchLeaderboard}>
            ↻ Refresh
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading sales rankings...</p>
          </div>
        ) : leaderboardData.length === 0 ? (
          <Alert variant="info" className="m-3">
            <strong>No sales data yet.</strong> Be the first to record sales!
          </Alert>
        ) : (
          <Table hover responsive className="mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="ps-4" style={{ width: '80px' }}>Rank</th>
                <th>Learner</th>
                {!isLearnerView && <th className="text-center">Cohort</th>}
                {!isLearnerView && <th className="text-center">Transactions</th>}
                {!isLearnerView && <th className="text-center">Items Sold</th>}
                {!isLearnerView && <th className="text-center">Last Sale</th>}
                {!isLearnerView && <th className="text-center">Performance</th>}
                <th className="text-end pe-4">Total Sales</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.map((user) => (
                <tr
                  key={user.userId}
                  className={currentUser?.uid === user.userId ? "table-primary" : ""}
                  style={user.rank <= 3 ? { fontWeight: '500' } : {}}
                >
                  <td className="ps-4">{getRankBadge(user.rank)}</td>
                  <td>
                    <div>
                      <strong>{user.userName}</strong>
                      {currentUser?.uid === user.userId && (
                        <Badge bg="primary" className="ms-2">You</Badge>
                      )}
                      {!isLearnerView && <div className="small text-muted">{user.userEmail}</div>}
                    </div>
                  </td>
                  {!isLearnerView && (
                    <td className="text-center small text-muted">
                      {user.cohortName || '-'}
                    </td>
                  )}
                  {!isLearnerView && (
                    <td className="text-center">
                      <Badge bg="info" pill>{user.transactionCount}</Badge>
                    </td>
                  )}
                  {!isLearnerView && (
                    <td className="text-center">
                      <Badge bg="secondary" pill>{user.itemCount}</Badge>
                    </td>
                  )}
                  {!isLearnerView && (
                    <td className="text-center small text-muted">
                      {user.lastSubmission?.toLocaleDateString('en-ZA', {
                        month: 'short',
                        day: 'numeric'
                      }) || '-'}
                    </td>
                  )}
                  {!isLearnerView && (
                    <td className="text-center">
                      {getPerformanceBadge(user.totalSales)}
                    </td>
                  )}
                  <td className="text-end pe-4">
                    <strong className="text-success" style={{ fontSize: '1.1em' }}>
                      R {user.totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      {!loading && leaderboardData.length > 0 && (
        <Card.Footer className="bg-light text-muted small">
          <div className="d-flex justify-content-between">
            <span>
              Total participants: <strong>{leaderboardData.length}</strong>
            </span>
            <span>
              Combined sales: <strong className="text-success">
                R {leaderboardData.reduce((sum, user) => sum + user.totalSales, 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </span>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};

export default TakeALotLeaderboard;
